/**
 * Session actors / flow moderators list row UI.
 * - `flowSlotAssets` — slot-assets column only
 * - `flowScheduleInline` — schedule tab / edit session slot (narrow + wide details)
 */
export type ContributorListDisplayVariant = 'default' | 'flowSlotAssets' | 'flowScheduleInline';

/** Media list / widget presentation (e.g. flow Control tab playout list). */
export type MediaListDisplayVariant = 'default' | 'flowControl';

/** Fixed icon slot in the flow Control media actions rail (next-show-mode / advance / display). */
export const FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX = 44;
/** Play column: divider + icon, right-aligned in every row. */
export const FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX = 52;
/** Secondary slots before play: auto-loop dwell (when enabled), advance mode, next show-mode. */
export const FLOW_CONTROL_MEDIA_SECONDARY_SLOT_COUNT = 3;
/** Secondary slots + play column — divider always at the same offset from the right. */
export const FLOW_CONTROL_MEDIA_ACTIONS_RAIL_WIDTH_PX =
  FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX * FLOW_CONTROL_MEDIA_SECONDARY_SLOT_COUNT +
  FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX;

export function flowControlActionsRailWidthPx(secondaryCount: number): number {
  return FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX * Math.max(0, secondaryCount) + FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX;
}

/** Vertical gap between rows in the Control tab slot list (default list uses `1`). */
export const FLOW_CONTROL_SLOT_LIST_GAP = 1.5;

/** Flow detail panel modes (flow-level views when no slot is selected) */
export type FlowDetailMode =
  | 'flowSettings'
  | 'flowSchedule'
  | 'flowSlotAssets'
  | 'flowContributors'
  | 'flowConfiguration'
  | 'flowControl';

/** Maps FlowDetailMode to URL view param for persistence */
export const FLOW_DETAIL_MODE_TO_VIEW: Record<FlowDetailMode, string> = {
  flowSettings: 'settings',
  flowSchedule: 'schedule',
  flowSlotAssets: 'assets',
  flowContributors: 'contributors',
  flowConfiguration: 'configuration',
  flowControl: 'control',
};

/** Start-row chrome shared by slot-assets and control tabs (flow title, day read-only, etc.). */
export function isFlowStartRowSlotAssetsPresentation(mode?: FlowDetailMode): boolean {
  return mode === 'flowSlotAssets' || mode === 'flowControl';
}

/** Maps URL view param back to FlowDetailMode */
export const VIEW_TO_FLOW_DETAIL_MODE: Record<string, FlowDetailMode> = {
  settings: 'flowSettings',
  schedule: 'flowSchedule',
  assets: 'flowSlotAssets',
  contributors: 'flowContributors',
  configuration: 'flowConfiguration',
  control: 'flowControl',
};
